// Shared constants + insight engine for PSI Report (Advanced + Basic)
// DO NOT change any formulas or scoring — this module only reads results.

export const PARAM_KEYS = [
  "gwp", "water", "pollution", "circularity", "risk", "social",
  "ecosystem", "economic", "certifications", "time", "cost", "governance",
];

export const PL = {
  gwp: "GWP Climate Impact",
  water: "Water Impact",
  pollution: "Pollution Burden",
  circularity: "Material Circularity",
  risk: "Operational Risk",
  social: "Social Responsibility",
  ecosystem: "Ecosystem Impact",
  economic: "Economic Viability",
  certifications: "Certifications & Ethics",
  time: "Estimated Time",
  cost: "Estimated Cost",
  governance: "Governance",
};

export const PL_SHORT = {
  gwp: "GWP", water: "Water", pollution: "Pollution", circularity: "Circularity",
  risk: "Risk", social: "Social", ecosystem: "Ecosystem", economic: "Economic",
  certifications: "Certif.", time: "Time", cost: "Cost", governance: "Governance",
};

// Canonical units used by the calculation engine and benchmark contracts.
export const PARAM_UNITS = {
  gwp: "Kg CO₂-eq / Kg product",
  water: "L / Kg product",
  pollution: "Points / Kg product",
  circularity: "%",
  risk: "Score (1–10)",
  social: "Score (1–10)",
  ecosystem: "Score (1–10)",
  economic: "Score (1–10)",
  certifications: "Score (1–10)",
  time: "Years",
  cost: "INR / functional unit",
  governance: "Score (1–10)",
};
export const PARAM_AGG = {
  gwp: "sum", water: "sum", pollution: "avg",
  circularity: "avg",
  risk: "avg", social: "avg", ecosystem: "avg", economic: "avg",
  certifications: "avg",
  time: "avg", cost: "avg",
  governance: "avg",
};
export const PARAM_UNIT_SHORT = {
  gwp: "Kg CO₂-eq/kg", water: "L/kg", pollution: "Pts/kg",
  circularity: "%",
  risk: "Score", social: "Score", ecosystem: "Score",
  economic: "Score", certifications: "Score",
  time: "Years", cost: "INR/FU",
  governance: "Score",
};

/**
 * Compute horizontal cumulation (sum or average) of the RAW user-entered
 * values for each parameter across all stages/substages in `stageInputs`.
 * Optionally merges in `extraRows` — an array of additional per-parameter
 * value maps (e.g. country-average values for raw-material-extraction,
 * raw-material-processing, eol-disposal) that should contribute to the
 * aggregation but are not shown in the table.
 * Pure display helper — does NOT touch scoring logic or benchmarks.
 * Returns { [paramKey]: { value: number, method: "sum"|"avg", count: number,
 *                         userCount: number, inferredCount: number } }
 */
export function aggregateStageInputs(stageInputs, extraRows = []) {
  const out = {};
  if (!stageInputs) stageInputs = {};
  const keys = Object.keys(stageInputs);
  // Filter only substages the user actually filled (so pre-filled 0s
  // from fresh drafts don't pollute the average)
  PARAM_KEYS.forEach((pk) => {
    const userVals = keys
      .map((k) => {
        const v = stageInputs[k]?.[pk];
        return typeof v === "number" ? v : (v != null ? Number(v) : null);
      })
      .filter((v) => v != null && !isNaN(v));
    const extraVals = (extraRows || [])
      .map((r) => {
        const v = r?.[pk];
        return typeof v === "number" ? v : (v != null ? Number(v) : null);
      })
      .filter((v) => v != null && !isNaN(v));
    const allVals = [...userVals, ...extraVals];
    if (!allVals.length) {
      out[pk] = {
        value: 0, method: PARAM_AGG[pk],
        count: 0, userCount: 0, inferredCount: 0,
      };
      return;
    }
    const sum = allVals.reduce((a, b) => a + b, 0);
    const value = PARAM_AGG[pk] === "sum" ? sum : sum / allVals.length;
    out[pk] = {
      value,
      method: PARAM_AGG[pk],
      count: allVals.length,
      userCount: userVals.length,
      inferredCount: extraVals.length,
    };
  });
  return out;
}

/**
 * Build country-average "inferred" rows for Raw Material Extraction,
 * Raw Material Processing, and End-of-Life Disposal based on the BOM.
 * For each phase, computes the mass-fraction-weighted average of per-
 * material per-country benchmark values, producing a single row with
 * the same parameter keys as user stage inputs.
 *
 * Inputs:
 *   bom          — array of { material, country, weight_kg, mass_fraction? }
 *   countryData  — object { [country]: { [material]: { [phase]: { [paramKey]: number } } } }
 *                  (phases are keyed "extraction" | "processing" | "disposal")
 * Returns:
 *   [
 *     { _source: "extraction",  gwp, water, pollution, circularity, ... },
 *     { _source: "processing",  ... },
 *     { _source: "disposal",    ... },
 *   ]
 * Phases with no matching benchmark rows are omitted.
 */
export function buildCountryInferredRows(bom, countryData) {
  if (!bom || !bom.length || !countryData) return [];
  // Compute mass fractions if not already present
  const totalMass = bom.reduce(
    (acc, b) => acc + (Number(b.weight_kg) || 0) * (Number(b.quantity) || 1),
    0
  ) || 1;
  const frac = (b) => {
    if (typeof b.mass_fraction === "number") return b.mass_fraction;
    return ((Number(b.weight_kg) || 0) * (Number(b.quantity) || 1)) / totalMass;
  };
  const phases = ["extraction", "processing", "disposal"];
  const rows = [];
  phases.forEach((phase) => {
    const weightedSum = {};
    const weightTotal = {};
    bom.forEach((b) => {
      const cd = countryData?.[b.country]?.[b.material]?.[phase];
      if (!cd) return;
      const w = frac(b);
      PARAM_KEYS.forEach((pk) => {
        const v = cd[pk];
        if (v == null || isNaN(Number(v))) return;
        weightedSum[pk] = (weightedSum[pk] || 0) + Number(v) * w;
        weightTotal[pk] = (weightTotal[pk] || 0) + w;
      });
    });
    if (!Object.keys(weightedSum).length) return;
    const row = { _source: phase };
    PARAM_KEYS.forEach((pk) => {
      if (weightTotal[pk]) {
        row[pk] = weightedSum[pk] / weightTotal[pk];
      }
    });
    rows.push(row);
  });
  return rows;
}

// Substage keys that should be hidden from the raw-input table but whose
// country-average values still contribute to the cumulated totals.
export const HIDDEN_FROM_TABLE_SUBSTAGES = [
  "raw_material_extraction",
  "raw_material_processing",
  "eol_disposal",
];

export const PARAM_BUCKET = {
  gwp: "environmental", water: "environmental", pollution: "environmental",
  circularity: "environmental", ecosystem: "environmental",
  risk: "social", social: "social",
  certifications: "governance", governance: "governance",
  economic: "economic", time: "economic", cost: "economic",
};

export const STAGES_ORDER = ["raw_material", "manufacturing", "use_phase", "end_of_life", "logistics"];
export const STAGE_FULL = {
  raw_material: "Raw Material",
  manufacturing: "Manufacturing",
  use_phase: "Use Phase",
  end_of_life: "End-of-Life",
  logistics: "Logistics",
};
export const STAGE_SHORT = {
  raw_material: "Raw Mat.", manufacturing: "Mfg", use_phase: "Use",
  end_of_life: "EOL", logistics: "Logistics",
};

export const ESGE_COLORS = {
  environmental: "#43A047",
  social: "#1976D2",
  governance: "#F57F17",
  economic: "#7B1FA2",
};

export const getScoreColor = (s) =>
  s >= 8 ? "#1B5E20" : s >= 6 ? "#388E3C" : s >= 4 ? "#F57F17" : s >= 2 ? "#EF6C00" : "#D32F2F";

export const heatColor = (v) =>
  v >= 8 ? "#C8E6C9" : v >= 6 ? "#DCEDC8" : v >= 4 ? "#FFF9C4" : v >= 2 ? "#FFCCBC" : "#FFCDD2";

export const classifyStage = (si) => {
  if (si >= 4) return { label: "Highly Sustainable", color: "#388E3C", icon: "🟢" };
  if (si >= 3) return { label: "Moderate", color: "#F57F17", icon: "🟡" };
  return { label: "Critical", color: "#D32F2F", icon: "🔴" };
};

/**
 * Normalise parameter_scores into a stage-aggregated view for chart consumers.
 * Advanced: parameter_scores keyed by substage -> averages across substages of a stage.
 * Basic: parameter_scores keyed by stage -> passthrough.
 * Returns: { stageKey: { param: { raw, adjusted, transparency } } }
 */
export function buildParamsByStage(results, mode) {
  const paramScores = results?.parameter_scores || {};
  const out = {};
  const SUBSTAGE_TO_STAGE = {
    raw_material_extraction: "raw_material",
    raw_material_processing: "raw_material",
    manufacturing_pre_processing: "manufacturing",
    manufacturing_production: "manufacturing",
    use_phase_operation: "use_phase",
    use_phase_maintenance: "use_phase",
    eol_recycling: "end_of_life",
    eol_recovery: "end_of_life",
    eol_disposal: "end_of_life",
    logistics_transportation: "logistics",
    logistics_storage: "logistics",
  };

  if (mode === "basic") {
    for (const stage of Object.keys(paramScores)) out[stage] = paramScores[stage];
    return out;
  }

  // Advanced: use the saved backend substage weights, renormalized over applicable values.
  const acc = {};
  for (const [ss, params] of Object.entries(paramScores)) {
    const stage = SUBSTAGE_TO_STAGE[ss];
    if (!stage) continue;
    if (!acc[stage]) acc[stage] = {};
    for (const [pk, info] of Object.entries(params)) {
      if (!acc[stage][pk]) acc[stage][pk] = { rawSum: 0, adjSum: 0, tSum: 0, n: 0 };
      const weight = results?.stage_esge?.[stage]?.substage_weights?.[ss];
      const w = Number.isFinite(weight) ? weight : 1; // Legacy display only, labelled separately.
      acc[stage][pk].rawSum += (info.raw || 0) * w;
      acc[stage][pk].adjSum += (info.adjusted || 0) * w;
      acc[stage][pk].tSum += (info.transparency || 0) * w;
      acc[stage][pk].n += w;
    }
  }
  for (const [stage, params] of Object.entries(acc)) {
    out[stage] = {};
    for (const [pk, v] of Object.entries(params)) {
      const n = v.n || 1;
      out[stage][pk] = {
        raw: v.rawSum / n,
        adjusted: v.adjSum / n,
        transparency: v.tSum / n,
      };
    }
  }
  return out;
}

/**
 * Product-level bucket roll-up (E/S/G/Eco) from stage_esge weighted by stage weights.
 */
export function productBuckets(results) {
  const pE = { environmental: 0, social: 0, governance: 0, economic: 0 };
  const sw = results?.stage_weights || {};
  const se = results?.stage_esge || {};
  for (const [stg, info] of Object.entries(se)) {
    const w = sw[stg] || 0;
    for (const b of Object.keys(pE)) pE[b] += (info.buckets?.[b] || 0) * w;
  }
  const pESGE = 0.5 * pE.environmental + 0.2 * pE.social + 0.2 * pE.governance + 0.1 * pE.economic;
  return { ...pE, esge: pESGE };
}

/**
 * Rankings: sort stages by parameter impact (e.g. gwp, pollution, governance).
 * Uses paramsByStage adjusted score. Higher adjusted = better (all normalized to 0-10 "good").
 */
export function rankStagesByParam(paramsByStage, paramKey, ascending = true) {
  const arr = Object.entries(paramsByStage).filter(([, params]) => params[paramKey]?.adjusted != null).map(([stage, params]) => ({
    stage,
    value: params[paramKey]?.adjusted ?? 0,
  }));
  arr.sort((a, b) => (ascending ? a.value - b.value : b.value - a.value));
  return arr;
}

/**
 * Parameter dominance: top3 best & worst params per stage.
 */
export function paramDominance(paramsByStage) {
  const out = {};
  for (const [stage, params] of Object.entries(paramsByStage)) {
    const arr = Object.entries(params).map(([pk, v]) => ({ pk, adj: v.adjusted ?? 0 }));
    const sorted = [...arr].sort((a, b) => b.adj - a.adj);
    out[stage] = {
      top: sorted.slice(0, 3),
      bottom: sorted.slice(-3).reverse(),
    };
  }
  return out;
}

/**
 * Transparency-adjusted potential: if transparency were 10 everywhere, PSI could rise by Δ.
 */
export function transparencyUplift(paramsByStage, stageWeights) {
  // Approx: per stage, mean(raw) - mean(adjusted) ≈ uplift if T=10.
  let uplift = 0;
  for (const [stage, params] of Object.entries(paramsByStage)) {
    const w = stageWeights?.[stage] || 0;
    const diffs = Object.values(params).map((p) => (p.raw || 0) - (p.adjusted || 0));
    if (!diffs.length) continue;
    const avgDiff = diffs.reduce((a, b) => a + b, 0) / diffs.length;
    uplift += avgDiff * w;
  }
  return Math.max(0, uplift);
}

/**
 * Rule-based insight engine. Returns ordered list of {severity, icon, text, category}.
 * Severity: 'risk' | 'strength' | 'insight' | 'opportunity'
 */
export function generateInsights({ results, paramsByStage, mode, bom = [] }) {
  const out = [];
  const R = results || {};
  const si = R.stage_si || {};
  const sw = R.stage_weights || {};
  const se = R.stage_esge || {};
  const upsi = R.upsi || 0;

  const siArr = Object.entries(si).filter(([k]) => k);
  if (!siArr.length) return out;

  const [worstStage, worstSi] = siArr.reduce((a, b) => (a[1] < b[1] ? a : b));
  const [bestStage, bestSi] = siArr.reduce((a, b) => (a[1] > b[1] ? a : b));
  const critArr = Object.entries(se).map(([k, v]) => [k, v.criticality || 0]);
  const [critStage, critVal] = critArr.length
    ? critArr.reduce((a, b) => (a[1] > b[1] ? a : b))
    : [null, 0];

  // 1) Worst stage
  out.push({
    severity: "risk",
    icon: "⚠️",
    category: "Weakness",
    text: `${STAGE_FULL[worstStage]} is the weakest lifecycle stage at SI ${worstSi.toFixed(2)}. Prioritise interventions here — it carries ${((sw[worstStage] || 0) * 100).toFixed(1)}% weight and drags the overall PSI down by ≈ ${((10 - worstSi) * (sw[worstStage] || 0)).toFixed(2)} pts.`,
  });

  // 2) Best stage with leverage note
  out.push({
    severity: "strength",
    icon: "✅",
    category: "Strength",
    text: `${STAGE_FULL[bestStage]} leads at SI ${bestSi.toFixed(2)}${((sw[bestStage] || 0) * 100) > 20 ? ` and holds ${((sw[bestStage] || 0) * 100).toFixed(1)}% weight — a high-leverage strength to protect and market.` : "."}`,
  });

  // 3) Criticality hotspot
  if (critStage && critVal > 4) {
    out.push({
      severity: "risk",
      icon: "🔍",
      category: "Criticality",
      text: `${STAGE_FULL[critStage]} shows high criticality (${critVal.toFixed(2)}) — even small failures here propagate to total PSI. Recommend adding redundancy or tighter controls.`,
    });
  }

  // 4) Governance weakness check
  for (const [stg, info] of Object.entries(se)) {
    const g = info.buckets?.governance || 0;
    if (g < 3) {
      out.push({
        severity: "risk",
        icon: "⚠️",
        category: "Governance",
        text: `${STAGE_FULL[stg]} has weak governance (${g.toFixed(2)}) — obtain third-party certifications or publish compliance audits to lift G and overall ESGE.`,
      });
      break;
    }
  }

  // 5) Greenwashing risk (E high but G low)
  const pb = productBuckets(R);
  if (pb.environmental > 6 && pb.governance < 3) {
    out.push({
      severity: "risk",
      icon: "⚠️",
      category: "Greenwashing Risk",
      text: `Environmental score (${pb.environmental.toFixed(2)}) is strong but Governance (${pb.governance.toFixed(2)}) is weak — this gap signals greenwashing risk to auditors. Invest in verification and certifications.`,
    });
  }

  // 6) Parameter dominance — worst parameter across product
  const paramAvg = {};
  for (const [, params] of Object.entries(paramsByStage)) {
    for (const [pk, v] of Object.entries(params)) {
      if (!paramAvg[pk]) paramAvg[pk] = { sum: 0, n: 0 };
      paramAvg[pk].sum += v.adjusted || 0;
      paramAvg[pk].n += 1;
    }
  }
  const avgPairs = Object.entries(paramAvg).map(([pk, v]) => [pk, v.sum / v.n]);
  if (avgPairs.length) {
    avgPairs.sort((a, b) => a[1] - b[1]);
    const [worstP, worstPv] = avgPairs[0];
    const [bestP, bestPv] = avgPairs[avgPairs.length - 1];
    out.push({
      severity: "opportunity",
      icon: "🔧",
      category: "Parameter Focus",
      text: `${PL[worstP]} is the lowest-scoring parameter overall (${worstPv.toFixed(2)}). Targeted improvement here — e.g. cleaner energy for GWP, water recycling for Water — can raise PSI by ≈ ${((10 - worstPv) / 12).toFixed(2)} pts.`,
    });
    out.push({
      severity: "strength",
      icon: "✅",
      category: "Parameter Strength",
      text: `${PL[bestP]} is the strongest parameter (${bestPv.toFixed(2)}). Feature this in ESG disclosures and marketing.`,
    });
  }

  // 7) Pollution ranking
  const pollArr = Object.entries(paramsByStage)
    .map(([stage, p]) => ({ stage, v: p.pollution?.adjusted ?? 10 }))
    .sort((a, b) => a.v - b.v);
  if (pollArr.length) {
    const worst = pollArr[0];
    out.push({
      severity: "insight",
      icon: "🔍",
      category: "Pollution Hotspot",
      text: `${STAGE_FULL[worst.stage]} is the most polluting stage (Pollution Burden score ${worst.v.toFixed(2)}). Reducing pollutants here delivers the fastest measurable PSI gain.`,
    });
  }

  // 8) Transparency uplift potential
  const upl = transparencyUplift(paramsByStage, sw);
  if (upl > 0.2) {
    out.push({
      severity: "opportunity",
      icon: "🔧",
      category: "Data Reliability",
      text: `Upgrading data transparency to third-party verified (T=10) everywhere could raise PSI by ≈ ${upl.toFixed(2)} pts. Audit-grade data is both low-effort and high-leverage.`,
    });
  }

  // 9) PSI rating insight
  const band = upsi >= 8 ? "excellent" : upsi >= 6 ? "good" : upsi >= 4 ? "moderate" : "poor";
  out.push({
    severity: band === "poor" ? "risk" : band === "moderate" ? "insight" : "strength",
    icon: band === "poor" ? "⚠️" : band === "moderate" ? "🔍" : "✅",
    category: "Overall",
    text: `Overall PSI of ${upsi.toFixed(2)} places this product in the "${band.toUpperCase()}" band. ${
      band === "excellent"
        ? "Maintain through annual audits and continuous improvement."
        : band === "good"
        ? `Close the gap to Excellent by raising ${STAGE_FULL[worstStage]}.`
        : band === "moderate"
        ? `Focus on ${STAGE_FULL[worstStage]} and top-2 weakest parameters to cross into Good.`
        : "Urgent intervention needed — weakest stage and governance gaps must be addressed first."
    }`,
  });

  // 10) BOM leverage (material with highest mass share)
  if (bom && bom.length) {
    const topMat = [...bom].sort((a, b) => (b.mass_fraction || 0) - (a.mass_fraction || 0))[0];
    if (topMat && (topMat.mass_fraction || 0) > 0.25) {
      out.push({
        severity: "opportunity",
        icon: "🔧",
        category: "BOM Leverage",
        text: `${topMat.material} carries ${(((topMat.mass_fraction || 0)) * 100).toFixed(1)}% of product mass — switching to a recycled or lower-impact alternative for this single material can move Raw Material & EOL stages meaningfully.`,
      });
    }
  }

  // Basic-only: logistics check (since Basic has only 3 user-input stages)
  if (mode === "basic") {
    const log = si.logistics ?? 10;
    if (log < 4) {
      out.push({
        severity: "risk",
        icon: "⚠️",
        category: "Logistics",
        text: `Logistics is underperforming (SI ${log.toFixed(2)}). Short-haul routing, electrified transport, or warehouse efficiency offer the clearest wins.`,
      });
    }
  }

  return out;
}

const SUBSTAGE_LABEL = {
  raw_material_extraction: "Raw Material — Extraction",
  raw_material_processing: "Raw Material — Processing",
  manufacturing_pre_processing: "Manufacturing — Pre-Processing",
  manufacturing_production: "Manufacturing — Production",
  use_phase_operation: "Use Phase — Operation",
  use_phase_maintenance: "Use Phase — Maintenance",
  eol_recycling: "End-of-Life — Recycling",
  eol_recovery: "End-of-Life — Recovery",
  eol_disposal: "End-of-Life — Disposal",
  logistics_transportation: "Supply Chain — Transportation",
  logistics_storage: "Supply Chain — Storage",
};
const SUBSTAGE_TO_STAGE_MAP = {
  raw_material_extraction: "raw_material", raw_material_processing: "raw_material",
  manufacturing_pre_processing: "manufacturing", manufacturing_production: "manufacturing",
  use_phase_operation: "use_phase", use_phase_maintenance: "use_phase",
  eol_recycling: "end_of_life", eol_recovery: "end_of_life", eol_disposal: "end_of_life",
  logistics_transportation: "logistics", logistics_storage: "logistics",
};

/** Sorted list of items (stages basic / substages advanced) by Criticality desc. */
export function criticalityLadder(results, mode) {
  const sw = results?.stage_weights || {};
  if (mode === "advanced") {
    const se = results?.substage_esge || {};
    const ssi = results?.ssi || {};
    const rows = Object.keys(se).map((ss) => {
      const stage = SUBSTAGE_TO_STAGE_MAP[ss];
      const subweight = results?.stage_esge?.[stage]?.substage_weights?.[ss];
      const weight = Number.isFinite(subweight) ? (sw[stage] || 0) * subweight : null;
      return {
        key: ss,
        name: SUBSTAGE_LABEL[ss] || ss,
        stageKey: stage,
        score: ssi[ss] || 0,
        criticality: se[ss]?.criticality || 0,
        contribution: results?.aggregation?.method === 'process_b' || weight == null ? null : (ssi[ss] || 0) * weight,
        contributionPct: weight,
      };
    });
    rows.sort((a, b) => b.criticality - a.criticality);
    return rows;
  }
  const se = results?.stage_esge || {};
  const si = results?.stage_si || {};
  const rows = STAGES_ORDER.filter((s) => si[s] != null).map((s) => ({
    key: s, name: STAGE_FULL[s], stageKey: s,
    score: si[s] || 0,
    criticality: se[s]?.criticality || 0,
    contribution: (si[s] || 0) * (sw[s] || 0),
    contributionPct: sw[s] || 0,
  }));
  rows.sort((a, b) => b.criticality - a.criticality);
  return rows;
}

/** Classify items into 🔴 Immediate / 🟡 Monitor / 🟢 Stable. */
export function actionZones(results, mode) {
  const items = criticalityLadder(results, mode);
  const out = { immediate: [], monitor: [], stable: [] };
  for (const it of items) {
    const c = it.criticality, s = it.score;
    if ((c >= 6 && s < 5) || s < 3) out.immediate.push(it);
    else if (c < 4 && s >= 6) out.stable.push(it);
    else out.monitor.push(it);
  }
  return out;
}

/** Variability per stage: min, max, spread across 12 params. */
export function variabilityByStage(paramsByStage) {
  const out = {};
  for (const [stage, params] of Object.entries(paramsByStage)) {
    const vals = Object.values(params).map((p) => p.adjusted ?? 0);
    if (!vals.length) continue;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    out[stage] = { min: +min.toFixed(2), max: +max.toFixed(2), spread: +(max - min).toFixed(2) };
  }
  return out;
}

/** Root cause: for each weak item (score < threshold), lowest 2 params. Works for stage or substage key space. */
export function rootCauses(paramsMap, scoresMap, threshold = 5) {
  const out = [];
  for (const [key, score] of Object.entries(scoresMap)) {
    if (score == null || score >= threshold) continue;
    const params = paramsMap[key] || {};
    const arr = Object.entries(params).map(([pk, v]) => ({ pk, adj: v.adjusted ?? 0 }));
    arr.sort((a, b) => a.adj - b.adj);
    const drivers = arr.slice(0, 2);
    if (!drivers.length) continue;
    out.push({ key, score, drivers });
  }
  return out;
}

/** Primary pollution driver (lowest of gwp/pollution/ecosystem) per stage. */
export function primaryPollutionDriver(paramsByStage) {
  const out = {};
  const envKeys = ["gwp", "pollution", "ecosystem", "water"];
  for (const [stage, params] of Object.entries(paramsByStage)) {
    const arr = envKeys.filter(pk => params[pk]?.adjusted != null).map((pk) => ({ pk, adj: params[pk].adjusted }));
    arr.sort((a, b) => a.adj - b.adj);
    out[stage] = arr[0];
  }
  return out;
}

/** Estimate PSI uplift for each high-leverage action. */
export function upsiGainPotentials(results, paramsByStage) {
  const sw = results?.stage_weights || {};
  const actions = [];

  // 1. Transparency uplift
  const upl = transparencyUplift(paramsByStage, sw);
  if (upl > 0.1) {
    actions.push({
      label: "Upgrade data transparency to third-party verified (T=10)",
      delta: +upl.toFixed(2),
      rationale: "Discount factor (Raw × T/10) lifted across all parameters.",
    });
  }

  // 2. Lift worst stage to best stage level
  const si = results?.stage_si || {};
  const siPairs = Object.entries(si);
  if (siPairs.length >= 2) {
    const sorted = [...siPairs].sort((a, b) => a[1] - b[1]);
    const [worstK, worstV] = sorted[0];
    const [, bestV] = sorted[sorted.length - 1];
    const w = sw[worstK] || 0;
    const delta = (bestV - worstV) * w;
    if (delta > 0.1) {
      actions.push({
        label: `Lift ${STAGE_FULL[worstK]} (SI ${worstV.toFixed(2)}) to parity with best stage (${bestV.toFixed(2)})`,
        delta: +delta.toFixed(2),
        rationale: `Stage weight ${(w * 100).toFixed(1)}% × (${bestV.toFixed(2)} − ${worstV.toFixed(2)}).`,
      });
    }
  }

  // 3. Governance in worst-governed stage
  const se = results?.stage_esge || {};
  const govArr = Object.entries(se).map(([k, v]) => [k, v.buckets?.governance || 0]);
  if (govArr.length) {
    govArr.sort((a, b) => a[1] - b[1]);
    const [worstK, worstG] = govArr[0];
    if (worstG < 5) {
      const w = sw[worstK] || 0;
      const delta = w * 0.2 * (6 - worstG);
      if (delta > 0.05) {
        actions.push({
          label: `Strengthen Governance in ${STAGE_FULL[worstK]} (${worstG.toFixed(2)} → 6.0)`,
          delta: +delta.toFixed(2),
          rationale: "Governance carries 0.2 weight in ESGE; certifications & audits deliver fastest lift.",
        });
      }
    }
  }

  // 4. Lift worst parameter across all stages
  const paramAvg = {};
  for (const params of Object.values(paramsByStage)) {
    for (const [pk, v] of Object.entries(params)) {
      if (!paramAvg[pk]) paramAvg[pk] = { sum: 0, n: 0 };
      paramAvg[pk].sum += v.adjusted || 0;
      paramAvg[pk].n += 1;
    }
  }
  const pairs = Object.entries(paramAvg).map(([pk, v]) => ({ pk, avg: v.sum / v.n }));
  if (pairs.length) {
    pairs.sort((a, b) => a.avg - b.avg);
    const worst = pairs[0];
    const delta = (6 - worst.avg) / 12;
    if (delta > 0.05) {
      actions.push({
        label: `Improve "${PL[worst.pk]}" across all stages (avg ${worst.avg.toFixed(2)} → 6.0)`,
        delta: +delta.toFixed(2),
        rationale: "Single parameter lift averaged across 12 parameters.",
      });
    }
  }

  actions.sort((a, b) => b.delta - a.delta);
  return actions;
}

/** ESG imbalance — largest gap between E/S/G/Eco product-level buckets. */
export function esgImbalance(results) {
  const pb = productBuckets(results);
  const rows = [
    { key: "environmental", label: "Environmental", value: pb.environmental },
    { key: "social", label: "Social", value: pb.social },
    { key: "governance", label: "Governance", value: pb.governance },
    { key: "economic", label: "Economic", value: pb.economic },
  ];
  const sorted = [...rows].sort((a, b) => b.value - a.value);
  const top = sorted[0], bot = sorted[sorted.length - 1];
  const gap = top.value - bot.value;
  let severity = "balanced";
  let narrative = "ESG buckets are reasonably balanced — no single dimension dominates.";
  if (gap >= 4) {
    severity = "severe";
    narrative = `${top.label} (${top.value.toFixed(2)}) is dramatically higher than ${bot.label} (${bot.value.toFixed(2)}) — ${(top.key === "environmental" && bot.key === "governance") ? "classic greenwashing risk pattern; auditors will flag this." : "prioritise lifting the weakest bucket to reduce structural ESG risk."}`;
  } else if (gap >= 2) {
    severity = "moderate";
    narrative = `${top.label} leads ${bot.label} by ${gap.toFixed(2)} points — rebalance to improve overall ESGE score.`;
  }
  return { rows, top, bot, gap: +gap.toFixed(2), severity, narrative };
}

/** Top-5 prioritised actions combining gain potentials + critical hotspots + BOM leverage. */
export function top5Actions({ results, paramsByStage, mode, bom = [] }) {
  if (mode === "advanced") {
    return Object.entries(results?.stage_si || {}).sort((a,b)=>a[1]-b[1]).slice(0,5).map(([stage,score],i)=>{
      const weakest=Object.entries(paramsByStage[stage] || {}).sort((a,b)=>a[1].adjusted-b[1].adjusted)[0];
      return {priority:i+1, title:`Review ${STAGE_FULL[stage]}${weakest ? `: ${ADVANCED_PL[weakest[0]]}` : ''}`,
        delta:null, impact:score<4 ? 'High' : score<7 ? 'Medium' : 'Low',
        rationale:`Stage score ${(score * 10).toFixed(2)}/100. Priority is based on current performance; the TOSS change requires a complete recalculation.`};
    });
  }
  const actions = [];
  const gains = upsiGainPotentials(results, paramsByStage);
  for (const g of gains.slice(0, 3)) {
    actions.push({ icon: "🔧", title: g.label, delta: g.delta, rationale: g.rationale });
  }
  const ladder = criticalityLadder(results, mode);
  const topHot = ladder.find((r) => r.criticality >= 5 && r.score < 5);
  if (topHot && !actions.some((a) => a.title.includes(topHot.name))) {
    actions.push({
      icon: "⚠️",
      title: `Priority intervention in ${topHot.name}`,
      delta: +(topHot.contributionPct * Math.max(0, 6 - topHot.score)).toFixed(2),
      rationale: `Criticality ${topHot.criticality.toFixed(2)}, current score ${topHot.score.toFixed(2)}. Failures here strongly propagate to total PSI.`,
    });
  }
  if (bom && bom.length) {
    const topMat = [...bom].sort((a, b) => (b.mass_fraction || 0) - (a.mass_fraction || 0))[0];
    if (topMat && (topMat.mass_fraction || 0) > 0.25) {
      actions.push({
        icon: "♻️",
        title: `Switch ${topMat.material} (${(((topMat.mass_fraction || 0)) * 100).toFixed(1)}% mass) to a lower-impact / recycled alternative`,
        delta: +((topMat.mass_fraction || 0) * 0.5).toFixed(2),
        rationale: "Highest-mass material drives Raw Material & EOL stages; substitution offers step-change improvement.",
      });
    }
  }
  return actions.slice(0, 5).map((a, i) => ({ ...a, priority: i + 1 }));
}


export const ADVANCED_PL = {...PL, time: 'Activity Duration', risk: 'Operational Safety'};
export const advancedUnit = unit => String(unit || '').replace(/1[–-]10/g, '0–10');
export const STAGE_SCORE_NOTE = 'Stage scores combine substage Parameter Averages using saved weights. When exclusions differ, averaging the displayed parameter scores may not reproduce the stage score.';
export function evidenceSummary(results) {
  const counts = [0,0,0,0];
  for (const params of Object.values(results?.parameter_scores || {})) for (const info of Object.values(params)) {
    const levels = info.sources?.length ? info.sources.map(s=>s.level) : [info.transparency];
    for (const level of levels) counts[level===2 ? 0 : level===5 ? 1 : level===10 ? 2 : 3]++;
  }
  const total = counts.reduce((a,b)=>a+b,0);
  const pct = n => total ? (100*n/total).toFixed(1) : '0.0';
  return `Evidence entries: ${pct(counts[0])}% estimated, ${pct(counts[1])}% self-reported, ${pct(counts[2])}% declared verified, ${pct(counts[3])}% unclassified. Entry counts, not score weights. Classifications and rubric approvals are user-supplied; this report is not independent verification.`;
}

export const coverageDescription = results => results?.coverage_policy?.startsWith('Only justified N/A')
  ? 'Excluded parameters and their recorded reasons are listed below. Applicable missing inputs may receive conservative assumed scores. All four ESGE buckets are required for each active substage.'
  : results?.coverage_policy;

export const stageScoreNote = results => results?.aggregation?.method === 'process_b'
  ? 'Process B: each stage combines 50% mean substage Parameter Avg. with 50% ESGE shortfall component. The product combines 50% mean stage score with 50% stage ESGE shortfall component. Active children have equal relevance; ESGE groups pass upward as arithmetic means. Criticality identifies priorities and does not determine aggregation weights.'
  : STAGE_SCORE_NOTE;
export const processBBreakdown = results => {
  const a = results?.aggregation;
  if (a?.method !== 'process_b') return [];
  return [...Object.entries(a.stages).map(([key, item]) => [STAGE_FULL[key] || key, item]), ['Final TOSS Index', a.product]];
};
